import { apiErrorText } from "../../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `orders` — P12-03's half of the design's Order screen.
 *
 * The order book as a shop owner works it: four figures, a status filter, a search box, the
 * table, and the three actions along the top — open the shop screen on a tablet, print the
 * open tickets, export a CSV.
 *
 * THE FOUR THINGS ON THIS SCREEN THAT ARE DECISIONS RATHER THAN MARKUP:
 *
 *  1. **"Open shop screen" produces a link with the capability in the FRAGMENT.** The key is
 *     a `shop.counter_access_key` — "a long random key in the URL, revocable, scoped to one
 *     shop" — and a fragment is never sent to a server, so it stays out of every access log
 *     between the tablet and the origin. The key is shown once, when a link is made — with
 *     Copy, a QR code for the tablet's camera and Open (review #124) — and a rotation that
 *     would revoke the tablet's current link asks first.
 *
 *  2. **The search box says what it searches.** There is no documented search parameter, so
 *     it filters the page that is loaded. A box that looked like it searched the whole table
 *     while searching one page would be the worse of the two lies, so its accessible name and
 *     its tooltip both say "on this page".
 *
 *  3. **Every row links back to its conversation**, which is the design's own promise. A row
 *     with no conversation — an order placed at the counter or through the API — says so
 *     rather than rendering a dead link.
 *
 *  4. **Nothing here can cancel an order.** Putting an order into an arbitrary state is not
 *     expressible from this screen; cancelling (with a reason) and the one-step undo live on
 *     the Order detail, where the consequence is spelled out (review #123).
 *
 * WHAT THE DESIGN SHOWS THAT IS NOT BUILT: **"Print open tickets".** There is no batch print
 * operation — `POST /v1/orders/{ref}/ticket` records one print of one order — so a live button
 * would fire N requests behind one click and write N `order_ticket` rows whose count nobody
 * asked for. It is drawn where the design puts it, disabled and marked "soon"; printing one
 * ticket lives on the Order detail, where the thing being printed is beside the button.
 *
 * AMENDED 2026-10-09 (design sync): the title is "Order book" alone, without the summary line; the
 * archive notice is one sentence with "Connect a POS" (the Versions tab, where the agent is edited)
 * and a dismiss that this browser remembers; each status is a coloured pill (New, In the kitchen,
 * Out for delivery, Delivered, Cancelled struck through); each row has a phone button beside
 * Open; the retention footnote is gone. The row read carries no conversation id (the list's
 * `CustomerOrderRow` has none — only the detail does), so the phone button opens the order, whose
 * page links to the call; reported as a gap.
 *
 * AMENDED 2026-10-09 (plan-015 ORD, gap G10): the row now carries its `conversation_id`, and — for a
 * holder of `recording.play` only — `recording_available`. The phone button plays the conversation's
 * recording where there is one this person may play, through the same audited grant History's player
 * uses (`accessRecording`: one `recording.played` audit entry per grant, asked for on the press and
 * never on render, reused while it lasts so pause and resume are one play). Otherwise — a Member or a
 * Viewer, who are not told whether a recording exists, or a conversation with none — it opens the
 * conversation in History. A row with no conversation draws no phone button.
 */
import { ApiError } from "@wetalk/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Dialog,
  Icon,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { money } from "@wetalk/domain";
import { useRef, useState, type ReactNode } from "react";

/** This browser's "I have read it" for the archive notice — a convenience, never state that matters. */
const NOTICE_DISMISSED_KEY = "wetalk.orders.posNoticeDismissed";

function noticeDismissed(): boolean {
  try {
    return window.localStorage.getItem(NOTICE_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberNoticeDismissed(): void {
  try {
    window.localStorage.setItem(NOTICE_DISMISSED_KEY, "1");
  } catch {
    // Storage refused (a private window): the notice simply returns next time.
  }
}

import { GLYPH } from "../../../../shell/glyph.js";
import { formatMoney } from "../../../../money.js";
import { useGateway } from "../../../../api/provider.js";
import {
  CUSTOMER_ORDER_NOT_PAID_ONLINE_LABEL,
  CUSTOMER_ORDER_NOT_PAID_ONLINE_STATE,
  CUSTOMER_ORDER_PAYMENT_STATE_LABEL,
  CUSTOMER_ORDER_PAYMENT_STATE_TONE,
  customerOrderPaymentStateCountText,
} from "../../../../api/slice/customer-order-payment.js";
import {
  CUSTOMER_ORDER_FILTER,
  customerOrderCountText,
  customerOrderRowAwaitsPayment,
  matchesCustomerOrderFilter,
  type CustomerOrderFilter,
  type CustomerOrderRowView,
  type CustomerOrderStatusView,
} from "./order-book-gateway.js";
import { useShopCustomerOrderStream } from "./order-book-live.js";
import { ShopLinkPanel } from "./shop-link-panel.js";
import "./orders.css";
import { WaitTimePanel } from "./wait-time-panel.js";

/**
 * Retry a failed order-book read once — but never a 404 (customer-portal review #97). A 404 is
 * an answer ("no such shop"), and asking again only doubles the console errors.
 */
export function retryOrderBookRead(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 404) return false;
  return failureCount < 1;
}

/** The query keys this tab owns. Prefixed, so an invalidation cannot reach another screen. */
export const ORDER_BOOK_KEY = {
  page: (voiceAgentId: string, filter: string) => ["order-book-page", voiceAgentId, filter] as const,
  summary: (voiceAgentId: string) => ["order-book-summary", voiceAgentId] as const,
};

/**
 * The label each filter chip carries. `all` is not a status, so it is named separately; nor is
 * "Not paid online" (OD-P6 (a); plan-006 FIN-B6): a new order whose online payment has not arrived
 * — its link still works, or the payment did not complete — kept out of "New", the kitchen's "To
 * accept", until it is paid, marked paid or switched to hand-over. The chip names both states at
 * once, so it borrows neither state's label.
 */
const FILTER_LABEL: Readonly<Record<CustomerOrderFilter, string>> = {
  get all() { return portalText("operations", "all_6a720856"); },
  get awaiting_payment() { return CUSTOMER_ORDER_NOT_PAID_ONLINE_LABEL(); },
  get received() { return portalText("operations", "new_6403f2b7"); },
  get preparing() { return portalText("operations", "in_the_kitchen_d4d0b524"); },
  get ready() { return portalText("operations", "ready_20c7c552"); },
  get out_for_delivery() { return portalText("operations", "out_for_delivery_111fb997"); },
  get delivered() { return portalText("operations", "delivered_eea956cd"); },
  get cancelled() { return portalText("operations", "cancelled_a1bf92ef"); },
};

/**
 * How a status draws: the design colours every order still in the kitchen's hands in the
 * accent, a delivered one neutral, and a cancelled one as an outline.
 */
function StatusTag({ status }: { readonly status: CustomerOrderStatusView }): ReactNode {
  usePortalTranslation("operations");
  // Design sync 2026-10-09: one pill colour per stage of the order's day.
  return (
    <span className="c-va-order-status" data-status={status}>
      {FILTER_LABEL[status]}
    </span>
  );
}

/**
 * The row's Status cell. A new order not paid online is not the kitchen's yet (OD-P6 (a)), so
 * instead of "New" it says its payment state in that state's own words — the words the header
 * counts it under (§18.28.8), never the chip's.
 */
function RowStatus({ row }: { readonly row: CustomerOrderRowView }): ReactNode {
  usePortalTranslation("operations");
  if (!customerOrderRowAwaitsPayment(row)) return <StatusTag status={row.status} />;
  return (
    <Tag status={CUSTOMER_ORDER_PAYMENT_STATE_TONE[row.paymentState]}>
      {CUSTOMER_ORDER_PAYMENT_STATE_LABEL[row.paymentState]}
    </Tag>
  );
}

/**
 * The header's payment clauses (§18.28.8): one per state of the "Not paid online" chip that holds
 * an order, in the label map's order — "1 awaiting online payment · 2 online payment not
 * completed". The counts are the chip's rows split by state, so they add up to the chip's count.
 * `null` while the rows are still loading.
 */
export function notPaidOnlineHeaderClauseOf(
  row: readonly Pick<CustomerOrderRowView, "status" | "paymentState">[] | undefined,
): readonly string[] | null {
  if (row === undefined) return null;
  return CUSTOMER_ORDER_NOT_PAID_ONLINE_STATE.flatMap((state) => {
    const count = row.filter((one) => customerOrderRowAwaitsPayment(one) && one.paymentState === state).length;
    return count === 0 ? [] : [customerOrderPaymentStateCountText(state, count)];
  });
}

/** History's search for one conversation — the same the Order screen's "Open the call" carries. */
function historySearchOf(conversation: string) {
  return {
    tab: "history" as const,
    range: "30d" as const,
    outcome: "all" as const,
    q: "",
    conversation,
    analyticsRange: "14d" as const,
    agentVersion: "",
    concern: "gaps" as const,
  };
}

/** The conversation id's first block, as History names a recording it cannot name by caller. */
function shortConversationIdOf(conversationId: string): string {
  return conversationId.split("-")[0] ?? conversationId;
}

/** A granted play: the URL (never logged, never stored past its window) and when it stops working. */
interface GrantedPlay {
  readonly url: string;
  readonly expiresAtMs: number;
}

/**
 * The table's one player (plan-015 G10). One `<audio>` for every row, so starting a second call
 * stops the first. A grant is asked for when somebody presses play — each grant writes a
 * `recording.played` entry in the account's audit log (design §8.8), so a render never asks — and is
 * reused per conversation while it lasts (with a margin), so pausing and resuming is one play.
 */
function useCustomerOrderConversationPlayer(onRefused: (row: CustomerOrderRowView) => void): {
  readonly element: ReactNode;
  /** The reference of the order whose call is playing, or `null`. */
  readonly playing: string | null;
  /** The reference whose grant is being asked for, or `null`. */
  readonly opening: string | null;
  readonly toggle: (row: CustomerOrderRowView, conversationId: string) => Promise<void>;
} {
  const gateway = useGateway();
  const audio = useRef<HTMLAudioElement | null>(null);
  const granted = useRef(new Map<string, GrantedPlay>());
  const [playing, setPlaying] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  async function urlOf(row: CustomerOrderRowView, conversationId: string): Promise<string | null> {
    const held = granted.current.get(conversationId);
    if (held !== undefined && held.expiresAtMs - Date.now() > 15_000) return held.url;
    setOpening(row.reference);
    try {
      const access = await gateway.accessRecording(conversationId);
      const next = { url: access.url, expiresAtMs: Date.parse(access.expiresAt) };
      granted.current.set(conversationId, next);
      return next.url;
    } catch {
      return null;
    } finally {
      setOpening(null);
    }
  }

  async function toggle(row: CustomerOrderRowView, conversationId: string): Promise<void> {
    const element = audio.current;
    if (element === null) return;
    if (playing === row.reference) {
      element.pause();
      setPlaying(null);
      return;
    }
    element.pause();
    setPlaying(null);
    const url = await urlOf(row, conversationId);
    if (url === null) {
      // Refused — retention removed it since the list was read, or the role changed: the call
      // itself is still in History, which says why there is nothing to play.
      onRefused(row);
      return;
    }
    // On the element only: mirroring the URL into React state re-renders the element with a new
    // `src`, which aborts the pending play() (History's player, review #83).
    if (element.getAttribute("src") !== url) element.src = url;
    try {
      await element.play();
      setPlaying(row.reference);
    } catch {
      setPlaying(null);
    }
  }

  const element = (
    <audio
      ref={audio}
      preload="none"
      onEnded={() => {
        setPlaying(null);
      }}
    />
  );
  return { element, playing, opening, toggle };
}

/**
 * The row's phone button (plan-015 G10): play where this person may play the recording, open the
 * call in History otherwise, and nothing at all for an order no conversation placed.
 */
function CustomerOrderConversationButton({
  row,
  voiceAgentId,
  player,
}: {
  readonly row: CustomerOrderRowView;
  /** The agent whose History holds the call: the row's own, else the tab's. */
  readonly voiceAgentId: string;
  readonly player: ReturnType<typeof useCustomerOrderConversationPlayer>;
}): ReactNode {
  usePortalTranslation("operations");
  const conversationId = row.conversationId;
  if (conversationId === null) return null;
  const short = shortConversationIdOf(conversationId);
  if (row.recordingAvailable === true) {
    const playing = player.playing === row.reference;
    return (
      <button
        type="button"
        className="btn btn-icon c-va-orders-call"
        aria-label={playing ? portalText("operations", "pause_the_call_this_order_came_from_ed0b796a") : portalText("operations", "play_the_call_this_order_came_from_3f35d619")}
        title={portalText("operations", "value_the_call_value_8ab1f38d", { v0: String(playing ? portalText("operations", "pause_781961bc") : portalText("operations", "play_5d12bd53")), v1: String(short) })}
        disabled={player.opening === row.reference}
        onClick={() => {
          void player.toggle(row, conversationId);
        }}
      >
        <Icon glyph={playing ? GLYPH.pause : GLYPH.phoneCall} size="lg" />
      </button>
    );
  }
  return (
    <Link
      to="/agents/$voiceAgentId"
      params={{ voiceAgentId }}
      search={historySearchOf(conversationId)}
      className="btn btn-icon c-va-orders-call"
      aria-label={portalText("operations", "open_the_call_this_order_came_from_in_history_31b97567")}
      title={portalText("operations", "open_the_call_value_its_transcript_in_history_8f2428b3", { v0: String(short) })}
    >
      <Icon glyph={GLYPH.phoneCall} size="lg" />
    </Link>
  );
}

export interface OrderBookTabProps {
  readonly voiceAgentId: string;
  readonly filter: CustomerOrderFilter;
  readonly search: string;
  readonly onFilter: (filter: CustomerOrderFilter) => void;
  readonly onSearch: (search: string) => void;
  /** "Connect a POS": where the agent is edited (its order destination is part of a version). */
  readonly onConnectPos: () => void;
}

export function OrderBookTab(props: OrderBookTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const player = useCustomerOrderConversationPlayer((refused) => {
    if (refused.conversationId === null) return;
    void navigate({
      to: "/agents/$voiceAgentId",
      params: { voiceAgentId: refused.voiceAgentId ?? props.voiceAgentId },
      search: historySearchOf(refused.conversationId),
    });
  });
  const [noticeHidden, setNoticeHidden] = useState(noticeDismissed);
  const [rotated, setRotated] = useState<string | null>(null);
  // Review #124: a new link revokes the tablet's current one, so it is confirmed first.
  const [confirmingRotation, setConfirmingRotation] = useState(false);

  const summary = useQuery({
    queryKey: ORDER_BOOK_KEY.summary(props.voiceAgentId),
    queryFn: () => gateway.getCustomerOrderSummary(props.voiceAgentId),
    retry: retryOrderBookRead,
  });
  // No shop yet (review #97): there is no book to page, so the table is not asked for at all.
  const hasShop = summary.data !== undefined && summary.data.shopId !== null;
  const page = useQuery({
    queryKey: ORDER_BOOK_KEY.page(props.voiceAgentId, props.filter),
    queryFn: () =>
      gateway.listCustomerOrders({
        voiceAgentId: props.voiceAgentId,
        filter: props.filter,
        search: props.search,
        cursor: null,
      }),
    enabled: hasShop,
    retry: retryOrderBookRead,
  });
  // The chips' counts come from the unfiltered page, so every chip says how many it holds.
  const everyCustomerOrder = useQuery({
    queryKey: ORDER_BOOK_KEY.page(props.voiceAgentId, "all"),
    queryFn: () =>
      gateway.listCustomerOrders({
        voiceAgentId: props.voiceAgentId,
        filter: "all",
        search: "",
        cursor: null,
      }),
    enabled: hasShop,
    retry: retryOrderBookRead,
  });

  // FR-PAY-11: a payment confirmed, a status moved at the counter — the tab re-reads on the shop's
  // stream, so a paid order leaves "Not paid online" without a reload.
  useShopCustomerOrderStream(hasShop ? (summary.data?.shopId ?? null) : null);

  const rotate = useMutation({
    mutationFn: (shopId: string) => gateway.rotateCounterAccessKey(shopId),
    onSuccess: (outcome) => {
      setConfirmingRotation(false);
      setRotated(outcome.counterAccessKey);
      void queryClient.invalidateQueries({ queryKey: ORDER_BOOK_KEY.summary(props.voiceAgentId) });
    },
  });

  // Review #90: a failed read is said, never an endless loading line. "This workspace has no
  // shop" is the order book's own answer, and it is the truth the owner needs to see.
  if (summary.error !== null) {
    // No shop yet is not a failure: the order book opens with the workspace's first confirmed
    // order (the runtime's order desk creates the shop then). Anything else is said as it is.
    if (summary.error instanceof ApiError && summary.error.status === 404 && summary.error.field === "workspace_id") {
      return (
        <p className="c-note">
          {portalText("operations", "no_orders_yet_the_order_book_opens_with_this_agent_s_first_64b023d7")}</p>
      );
    }
    return <p className="c-note c-note-critical">{portalText("operations", "the_order_book_could_not_be_opened_3d13a464")}{apiErrorText(summary.error, "")}</p>;
  }
  if (summary.data === undefined) {
    return <p className="c-note">{portalText("operations", "opening_the_order_book_9dc04bd6")}</p>;
  }
  const shop = summary.data;
  const shopId = shop.shopId;
  if (shopId === null) {
    return (
      <p className="c-note">
        {portalText("operations", "no_orders_yet_the_order_book_opens_with_this_agent_s_first_64b023d7")}</p>
    );
  }
  const wanted = props.search.trim().toLowerCase();
  const row = (page.data?.row ?? []).filter((one) =>
    wanted === ""
      ? true
      : one.reference.toLowerCase().includes(wanted) ||
        (one.partyName ?? "").toLowerCase().includes(wanted) ||
        (one.partyPhone ?? "").includes(props.search.trim()),
  );
  const countOf = (filter: CustomerOrderFilter): number | null => {
    const all = everyCustomerOrder.data?.row;
    if (all === undefined) return null;
    return all.filter((one) => matchesCustomerOrderFilter(one, filter)).length;
  };
  // OD-P6 (a): "waiting to be accepted" is the kitchen's column; an order not paid online is
  // counted apart — per payment state, in each state's own words — and joins it once paid.
  const waitingCount = countOf("received");
  const notPaidOnlineClause = notPaidOnlineHeaderClauseOf(everyCustomerOrder.data?.row);

  return (
    <div className="c-va-orders">
      {player.element}
      <div className="c-va-titlerow">
        <div>
          {/*
            Review #102: "1 order", not "1 orders". Review #123 and FR-PAY-10: "taken" is what has
            been paid — paid online net of refunds, orders marked paid, and cash or card-on-delivery
            orders once delivered — never an unpaid online order, delivered or not.
          */}
          <p className="c-va-subtitle" title={portalText("operations", "value_value_takenvaluevalue_75ae24dd", { v0: String(customerOrderCountText(shop.orderCount)), v1: String(formatMoney(shop.taken)), v2: String(waitingCount === null ? "" : portalText("operations", "value_waiting_to_be_accepted_12924981", { v0: String(String(waitingCount)) })), v3: String((notPaidOnlineClause ?? []).map((clause) => ` · ${clause}`).join("")) })}>
            {portalText("operations", "order_book_406bfc44")}</p>
        </div>
        <div className="c-va-actions">
          {/*
            ONE BUTTON, AND WHICH ONE IT IS DEPENDS ON WHETHER THIS BROWSER HOLDS THE KEY.

            The API returns a counter key exactly once, at rotation, and never on a read —
            it is a capability, and a console that redisplayed it would leave it in every
            screenshot of this tab. So the console cannot "open the shop screen" for a key
            it was never given. Rather than render a button that opens a broken link, the
            act is named for what it actually does: issue a link, which revokes whatever was
            on the counter before, and only then offer to open it.
          */}
          <Button
            variant="primary"
            disabled={rotate.isPending}
            onClick={() => {
              // A link already on the counter is revoked by a new one (review #124), so that
              // case asks first; the very first link revokes nothing and is made straight away.
              if (shop.counterAccessKeySet) setConfirmingRotation(true);
              else rotate.mutate(shopId);
            }}
          >
            <Icon glyph={GLYPH.deviceTablet} size="md" />
            <span>{shop.counterAccessKeySet ? portalText("operations", "new_shop_screen_link_2c243170") : portalText("operations", "create_the_shop_screen_link_e50b7bef")}</span>
          </Button>
          {/*
            There is no batch print — `POST /v1/orders/{ref}/ticket` records one print of one
            order — so the design's button is drawn where it stands, disabled, and says so.
            One ticket prints from the Order detail.
          */}
          <Button variant="secondary" disabled title={portalText("operations", "print_one_ticket_from_its_order_for_now_af55bd6f")}>
            <Icon glyph={GLYPH.printer} size="md" />
            <span>{portalText("operations", "print_open_tickets_soon_d2449d71")}</span>
          </Button>
          <a
            className="btn btn-secondary"
            href={gateway.customerOrderExportUrl(props.voiceAgentId)}
            download="orders.csv"
            title={portalText("operations", "every_order_with_its_payment_state_the_amount_paid_online_6d82c070")}
          >
            <Icon glyph={GLYPH.downloadSimple} size="md" />
            <span>{portalText("operations", "export_csv_5755f9ac")}</span>
          </a>
        </div>
      </div>

      {noticeHidden ? null : (
        <p className="c-va-orders-notice" role="note">
          <span className="c-va-orders-notice-icon">
            <Icon glyph={GLYPH.archive} size="xl" />
          </span>
          <span className="c-va-orders-notice-text">{portalText("operations", "orders_are_kept_here_because_no_pos_is_connected_1a9c4182")}</span>
          <a
            href="#"
            className="c-va-orders-notice-link"
            onClick={(event) => {
              event.preventDefault();
              props.onConnectPos();
            }}
          >
            {portalText("operations", "connect_a_pos_e498e37a")}</a>
          <button
            type="button"
            className="btn btn-icon c-va-orders-notice-dismiss"
            aria-label={portalText("operations", "dismiss_70afe9ef")}
            onClick={() => {
              rememberNoticeDismissed();
              setNoticeHidden(true);
            }}
          >
            <Icon glyph={GLYPH.x} size="md" />
          </button>
        </p>
      )}

      {/* 2026-09-30: what the agent tells callers about the wait, which the shop screen also changes. */}
      <WaitTimePanel gateway={gateway} shopId={shopId} />

      <Dialog
        open={confirmingRotation}
        title={portalText("operations", "make_a_new_shop_screen_link_6c4e9e3d")}
        onClose={() => {
          setConfirmingRotation(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmingRotation(false);
              }}
            >
              {portalText("operations", "keep_the_current_link_7b4c8e9e")}</Button>
            <Button
              variant="primary"
              disabled={rotate.isPending}
              onClick={() => {
                rotate.mutate(shopId);
              }}
            >
              {portalText("operations", "make_a_new_link_fd44711f")}</Button>
          </>
        }
      >
        <p>
          {portalText("operations", "the_link_the_counter_tablet_is_using_now_stops_working_the_2c2e6114")}</p>
      </Dialog>

      {rotated === null ? null : (
        <ShopLinkPanel
          url={gateway.shopScreenUrl(rotated)}
          onOpen={() => {
            // The capability rides in the fragment, so it never reaches a server log.
            globalThis.open(gateway.shopScreenUrl(rotated), "_blank", "noopener");
          }}
        />
      )}
      {rotate.error === null ? null : (
        <p className="c-orders-refusal" role="alert">
          <Icon glyph={GLYPH.warningCircle} size="md" />
          <span>{portalText("operations", "the_new_link_could_not_be_made_373fd835")}{apiErrorText(rotate.error, "")}</span>
        </p>
      )}

      <div className="c-va-orders-controls">
        <label className="c-va-search">
          <span className="wt-visually-hidden">{portalText("operations", "search_the_orders_on_this_page_b2e68fba")}</span>
          <span className="c-va-search-icon" aria-hidden="true">
            <Icon glyph={GLYPH.magnifyingGlass} size="md" />
          </span>
          <Input
            value={props.search}
            placeholder={portalText("operations", "order_number_name_or_phone_d9669212")}
            title={portalText("operations", "searches_the_orders_on_this_page_narrow_the_range_with_a_s_84f4ef5b")}
            onChange={(event) => {
              props.onSearch(event.currentTarget.value);
            }}
          />
        </label>
      </div>

      <div className="c-va-orders-stats">
        {[
          { label: portalText("operations", "orders_cded0933"), value: String(shop.orderCount) },
          { label: portalText("operations", "taken_c66c12b9"), value: formatMoney(shop.taken) },
          {
            label: portalText("operations", "average_order_d0ed5efb"),
            value:
              shop.takenCount <= 0
                ? "—"
                : formatMoney(money(Math.round(shop.taken.amountMinor / shop.takenCount), shop.taken.currency)),
          },
          { label: portalText("operations", "waiting_to_be_accepted_9fe060a1"), value: waitingCount === null ? "—" : String(waitingCount) },
        ].map((stat) => (
          <div className="c-va-orders-stat" key={stat.label}>
            <span className="c-va-orders-stat-value">{stat.value}</span>
            <span className="c-va-orders-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>

      <div className="c-va-chips">
        {CUSTOMER_ORDER_FILTER.map((one) => {
          const count = countOf(one);
          return (
            <button
              type="button"
              key={one}
              className="c-va-chip"
              aria-pressed={one === props.filter}
              onClick={() => {
                props.onFilter(one);
              }}
            >
              {FILTER_LABEL[one]}
              {count === null ? null : <span className="c-va-chip-count">{String(count)}</span>}
            </button>
          );
        })}
      </div>

      {row.length === 0 ? (
        <p className="c-note">
          {props.search.trim() === ""
            ? portalText("operations", "no_orders_in_this_range_yet_260734d8")
            : portalText("operations", "nothing_on_this_page_matches_that_2263feda")}
        </p>
      ) : (
        <div className="c-va-table-wrap">
          <Table caption={portalText("operations", "every_order_the_shop_has_taken_and_the_agent_that_took_it_bc0ee4bf")}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("operations", "order_1d75774c")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "caller_3fda6d67")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "what_they_ordered_41c5bb1a")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "agent_5ce2e6f4")}</TableHeaderCell>
                <TableHeaderCell numeric>{portalText("operations", "total_b25928c6")}</TableHeaderCell>
                <TableHeaderCell>{portalText("operations", "status_bae7d5be")}</TableHeaderCell>
                <TableHeaderCell>
                  <span className="wt-visually-hidden">{portalText("operations", "open_cf9b7706")}</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.map((one: CustomerOrderRowView) => (
                <TableRow key={one.reference}>
                  <TableCell>
                    <span className="c-va-orders-ref">{one.reference}</span>
                    <span className="c-va-orders-sub">{one.placedAtText}</span>
                  </TableCell>
                  <TableCell>
                    <span className="c-va-orders-who">{one.partyName ?? portalText("operations", "no_name_given_64df5fb7")}</span>
                    <span className="c-va-orders-sub c-va-orders-phone">
                      {/* Review #133: Members and Viewers never see caller details. */}
                      {shop.callerDetailVisible ? (one.partyPhone ?? portalText("operations", "no_number_1efd4932")) : portalText("operations", "number_hidden_for_your_role_cdb73291")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="c-va-orders-items">{one.itemSummary}</span>
                    <span className="c-va-orders-sub">{one.fulfilmentText}</span>
                  </TableCell>
                  <TableCell>
                    {/* Review 120: the shop's book holds every agent's orders; say whose each is. */}
                    <span className="c-va-orders-who">{one.voiceAgentName ?? portalText("operations", "placed_without_an_agent_ffff68dd")}</span>
                    {one.voiceAgentId !== null && one.voiceAgentId === props.voiceAgentId ? (
                      <span className="c-va-orders-sub">{portalText("operations", "this_agent_0685dc96")}</span>
                    ) : null}
                  </TableCell>
                  <TableCell numeric>
                    <span className="c-va-orders-total">{formatMoney(one.total)}</span>
                    <span className="c-va-orders-sub">{one.paymentText}</span>
                  </TableCell>
                  <TableCell>
                    <RowStatus row={one} />
                  </TableCell>
                  <TableCell numeric>
                    <span className="c-va-orders-open">
                      {/* plan-015 G10: play the call, open it in History, or — no conversation — nothing. */}
                      <CustomerOrderConversationButton row={one} voiceAgentId={one.voiceAgentId ?? props.voiceAgentId} player={player} />
                      {/* Review #112: the order page needs the agent to link back to it. */}
                      <Link
                        to="/orders/$customerOrderReference"
                        params={{ customerOrderReference: one.reference }}
                        search={{ voiceAgent: props.voiceAgentId }}
                      >
                        {portalText("operations", "open_cf9b7706")}</Link>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

    </div>
  );
}
